// Light settings panel for map editor

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Light settings panel component
 * Allows editing light properties in the map editor
 */
class LightSettingsPanel extends BaseComponent {
  static properties = {
    light: { type: Object },
  };

  static styles = css`
    :host {
      display: block;
    }

    .light-settings-panel {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      border: 1px solid #555;
      border-radius: 8px;
      padding: 16px;
      z-index: 1000;
      min-width: 250px;
      color: #fff;
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .panel-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: bold;
    }

    .dismiss-button {
      background: none;
      border: none;
      color: #fff;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .dismiss-button:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .setting-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .setting-row:last-child {
      margin-bottom: 0;
    }

    .setting-row label {
      font-size: 14px;
      margin-right: 12px;
    }

    .setting-row input[type='number'] {
      width: 80px;
      padding: 4px 8px;
      background: #333;
      border: 1px solid #555;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;
    }

    .setting-row input[type='checkbox'] {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    .setting-row input[type='number']:focus {
      outline: none;
      border-color: #0d6efd;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').Light|null} */
    this.light = null;
  }

  /** @param {Event} e */
  _handleUpdateBrightRadius(e) {
    const value = Number(/** @type {HTMLInputElement} */ (e.target).value);
    this.dispatchEvent(
      new CustomEvent('update-bright-radius', {
        detail: value,
        bubbles: true,
      }),
    );
  }

  /** @param {Event} e */
  _handleUpdateDimRadius(e) {
    const value = Number(/** @type {HTMLInputElement} */ (e.target).value);
    this.dispatchEvent(
      new CustomEvent('update-dim-radius', {
        detail: value,
        bubbles: true,
      }),
    );
  }

  _handleToggleIsOn() {
    this.dispatchEvent(new CustomEvent('toggle-is-on', { bubbles: true }));
  }

  _handleDismiss() {
    this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true }));
  }

  render() {
    if (!this.light) {
      return html``;
    }

    return html`
      <div class="light-settings-panel">
        <div class="panel-header">
          <h3>Light Settings</h3>
          <button class="dismiss-button" title="Close" @click="${this._handleDismiss}">×</button>
        </div>
        <div class="setting-row">
          <label for="bright-radius">Bright Radius:</label>
          <input
            id="bright-radius"
            type="number"
            min="0"
            step="1"
            .value="${this.light.brightRadius}"
            @input="${this._handleUpdateBrightRadius}"
          />
        </div>
        <div class="setting-row">
          <label for="dim-radius">Dim Radius:</label>
          <input
            id="dim-radius"
            type="number"
            min="0"
            step="1"
            .value="${this.light.dimRadius}"
            @input="${this._handleUpdateDimRadius}"
          />
        </div>
        <div class="setting-row">
          <label for="is-on">Light On:</label>
          <input id="is-on" type="checkbox" .checked="${this.light.isOn}" @change="${this._handleToggleIsOn}" />
        </div>
      </div>
    `;
  }
}

customElements.define('light-settings-panel', LightSettingsPanel);
